import {useManageMedicalConsentsMutation} from '../../../../api/services/lookupsService';
import {Alert, Box, Button, Table, TableBody, TableCell, TableHead, TableRow, Tooltip} from '@mui/material';
import {HOVER_BOX} from '../../../../shared-components/InfoHover';
import {SMALL_BLUE_BUTTON_SX} from '../SettingsPills';
import React, {useEffect, useRef, useState} from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../../shared-components/AdmicityForm';
import EditIcon from '@mui/icons-material/Edit';
import useNotification from '../../../../utility/hooks/useNotification';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import DescriptionSettings from './DescriptionSettings';
import SectionHeading from './SectionHeading';
import {DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS, PROFILE_SECTIONS_KEYS} from '../../../../constants/ProfileSectionsKeys';

function MedicalConsentSettings({
    medicalConsentOptions,
    title,
    description,
    onTitleChange,
    onDescriptionChange
}) {
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const formRef = useRef(null);
    const [introOpen, setIntroOpen] = useState(false);
    const [selectedConsent, setSelectedConsent] = useState({});
    const [activeConsents, setActiveConsents] = useState([]);
    const [saveValidationError, setSaveValidationError] = useState('');
    const {showErrorNotification, showSuccessNotification} = useNotification();

    const [manageConsents, {
        isSuccess,
        isError,
    }] = useManageMedicalConsentsMutation();

    useEffect(() => {
        setActiveConsents(medicalConsentOptions?.options);
    }, [medicalConsentOptions]);

    useEffect(() => {
        if (isError) {
            showErrorNotification('Failed to update medical consents');
        } else if (isSuccess) {
            showSuccessNotification(
                'Medical consents updated successfully. The Groups filter tree will reflect these changes after the next MIS synchronization.'
            );
        }
    }, [isSuccess, isError]);

    const normalizeTitle = (value) => value?.trim().toLowerCase();

    const getDuplicateTitleMessage = (value) => {
        const normalizedCurrent = normalizeTitle(value);
        if (!normalizedCurrent) {
            return '';
        }

        const duplicateCount = activeConsents.filter(consent => {
            const normalizedExisting = normalizeTitle(consent?.description);
            if (normalizedExisting !== normalizedCurrent) {
                return false;
            }

            return openAddDialog || consent?.description !== selectedConsent?.description;
        }).length;

        return duplicateCount > 0 ? 'This title already exists. Please use a unique title.' : '';
    };

    const textFields = [{
        label: 'Title',
        name: 'description',
        required: true,
        initialValue: selectedConsent?.description ?? undefined,
        validate: (value) => {
            const duplicateTitleMessage = getDuplicateTitleMessage(value);
            return {
                isValid: !!value && !!value.trim() && value.length <= 300 && !duplicateTitleMessage,
                message: getTitleValidationMessage(value, duplicateTitleMessage)
            };
        }
    }, {
        label: 'Summary',
        name: 'summary',
        required: false,
        multiline: true,
        rows: 3,
        nullableOnEmpty: true,
        initialValue: selectedConsent?.summary ?? undefined,
        validate: (value) => {
            return {
                isValid: !value || value.length <= 500,
                message: 'Summary cannot exceed 500 characters.'
            };
        }
    }];

    const getTitleValidationMessage = (value, duplicateTitleMessage = '') => {
        if (!value || value.length === 0) {
            return 'Please provide a title.';
        }

        if (!value.trim()) {
            return 'Title cannot contain only spaces.';
        }

        if (value.length > 300) {
            return 'Title cannot exceed 300 characters.';
        }

        if (duplicateTitleMessage) {
            return duplicateTitleMessage;
        }

        if (value.trim().length > 0 && value.length <= 300) {
            return '';
        }

        return 'Please provide a title.';
    };

    const getListValidationError = () => {
        const normalizedTitles = new Set();

        for (const consent of activeConsents) {
            const title = consent?.description ?? '';

            if (!title.trim()) {
                return 'Each medical consent must have a title. Titles cannot contain only spaces.';
            }

            if (title.length > 300) {
                return 'Medical consent titles cannot exceed 300 characters.';
            }

            const normalizedTitle = normalizeTitle(title);

            if (normalizedTitles.has(normalizedTitle)) {
                return 'Duplicate medical consent titles are not allowed.';
            }

            normalizedTitles.add(normalizedTitle);
        }

        return '';
    };

    const deleteConsent = (description) => {
        setActiveConsents(activeConsents.filter(e => e.description !== description));
    };

    const moveConsent = (index, targetIndex) => {
        if (targetIndex < 0 || targetIndex >= activeConsents.length) {
            return;
        }

        const next = [...activeConsents];
        const [item] = next.splice(index, 1);
        next.splice(targetIndex, 0, item);
        setActiveConsents(next);
    };

    const handleAdd = async (payload) => {
        setActiveConsents([...activeConsents, {
            description: payload.description,
            summary: payload.summary,
            id: null
        }]);
        setSaveValidationError('');
        handleFinishEditing();
    };

    const startEditing = (consent) => {
        setSelectedConsent(consent);
        setOpenEditDialog(true);
    };

    const handleEdit = async (payload) => {
        const newConsents = activeConsents.map(consent => {
            if (consent.description === selectedConsent.description) {
                return {
                    ...consent,
                    description: payload.description,
                    summary: payload.summary
                };
            }
            return consent;
        });

        setActiveConsents(newConsents);
        setSaveValidationError('');
        handleFinishEditing();
    };

    const handleFinishEditing = () => {
        setOpenAddDialog(false);
        setOpenEditDialog(false);
        setSelectedConsent(undefined);
    };

    const handleSave = () => {
        const validationError = getListValidationError();
        setSaveValidationError(validationError);

        if (validationError) {
            return;
        }

        manageConsents({activeMedicalConsents: activeConsents});
    };

    // Maria, 20 October 2026: OTC Medication laid out like Welfare: the heading parents read and the buttons on one
    // row, the introductory text in a box opened from that row, and the medicines in a neat table
    return (
        <Box>
            <Stack direction={{xs: 'column', md: 'row'}} justifyContent="space-between" alignItems={{md: 'center'}} gap={1} sx={{mb: 1}}>
                <SectionHeading
                    title={title}
                    onSave={onTitleChange}
                    testId="otc-heading"
                />
                <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                    <Tooltip
                        arrow
                        componentsProps={HOVER_BOX}
                        title="This text appears at the top of the Non-Prescription (OTC) Medication section on the Medical tab of the student profile."
                    >
                        <Button
                            sx={{height: 32, py: 0, px: 1.25, fontSize: 12, backgroundColor: '#fff'}}
                            variant="outlined"
                            data-testid="otc-intro-text"
                            onClick={() => setIntroOpen(true)}
                        >
                            Introductory text
                        </Button>
                    </Tooltip>
                    <Button
                        sx={SMALL_BLUE_BUTTON_SX}
                        variant="contained"
                        startIcon={<AddCircleOutlineIcon/>}
                        onClick={() => setOpenAddDialog(true)}
                    >
                        Add new
                    </Button>
                    <Tooltip
                        arrow
                        componentsProps={HOVER_BOX}
                        title="Changes to Medical OTC consent options are saved when Save is selected. The Groups filter tree will be refreshed with the updated values after the next MIS synchronization."
                    >
                        <Button sx={SMALL_BLUE_BUTTON_SX} variant="contained" onClick={handleSave} data-testid="otc-save">
                            Save
                        </Button>
                    </Tooltip>
                </Stack>
            </Stack>
            {saveValidationError && <Alert severity="error" sx={{mb: 1}}>{saveValidationError}</Alert>}
            <AdmicityDialog
                handleClose={() => setIntroOpen(false)}
                title="Introductory text"
                open={introOpen}
                maxWidth="md"
                actions={[{label: 'Close', onClick: () => setIntroOpen(false)}]}
            >
                <DescriptionSettings
                    fieldLabel={null}
                    rows={4}
                    description={description}
                    defaultDescription={DEFAULT_PROFILE_SECTIONS_DESCRIPTIONS[PROFILE_SECTIONS_KEYS.otcMedication]}
                    onDescriptionChange={onDescriptionChange}
                />
            </AdmicityDialog>
            <Box sx={{backgroundColor: '#fff', border: '1px solid #e3e8ef', borderRadius: 2, overflow: 'hidden'}}>
                <Table size="small" data-testid="otc-medicines">
                    <TableHead>
                        <TableRow sx={{'& th': {backgroundColor: '#e3f5f1', fontWeight: 600, py: 0.5, whiteSpace: 'nowrap'}}}>
                            <TableCell>Medicine</TableCell>
                            <TableCell>Summary</TableCell>
                            <TableCell align="center">Order</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {activeConsents.map((entity, index) => (
                            <TableRow
                                key={`medicalConsent-${index}`}
                                sx={{height: 32, '& td': {backgroundColor: index % 2 === 1 ? '#f1faf7' : '#fff', py: 0}}}
                            >
                                <TableCell sx={{whiteSpace: 'nowrap'}}>{entity?.description}</TableCell>
                                <TableCell sx={{color: 'text.secondary', whiteSpace: 'pre-line'}}>{entity?.summary || '-'}</TableCell>
                                <TableCell align="center" sx={{whiteSpace: 'nowrap'}}>
                                    <IconButton
                                        size="small"
                                        sx={{p: 0.5}}
                                        disabled={index === 0}
                                        onClick={() => moveConsent(index, index - 1)}
                                        aria-label="Move consent up"
                                    >
                                        <ArrowUpwardOutlinedIcon fontSize="small"/>
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        sx={{p: 0.5}}
                                        disabled={index === activeConsents.length - 1}
                                        onClick={() => moveConsent(index, index + 1)}
                                        aria-label="Move consent down"
                                    >
                                        <ArrowDownwardOutlinedIcon fontSize="small"/>
                                    </IconButton>
                                </TableCell>
                                <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                                    <IconButton size="small" sx={{p: 0.5}} onClick={() => startEditing(entity)} aria-label="Edit">
                                        <EditIcon fontSize="small"/>
                                    </IconButton>
                                    <IconButton size="small" sx={{p: 0.5}} onClick={() => deleteConsent(entity.description)} aria-label="Delete">
                                        <DeleteIcon fontSize="small"/>
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Box>
            <AdmicityDialog
                handleClose={handleFinishEditing}
                title={openAddDialog ? 'New Medical Consent' : 'Update Medical Consent'}
                open={openAddDialog || openEditDialog}
                actions={[]}
            >
                <AdmicityForm
                    ref={formRef}
                    textFields={textFields}
                    handleSubmit={(data) => openAddDialog
                        ? handleAdd(data)
                        : handleEdit(data)}
                    buttonText="Save"
                />
            </AdmicityDialog>
        </Box>
    );
}

MedicalConsentSettings.propTypes = {
    medicalConsentOptions: PropTypes.object,
    title: PropTypes.string,
    description: PropTypes.string,
    onTitleChange: PropTypes.func,
    onDescriptionChange: PropTypes.func
};

export default MedicalConsentSettings;
